'use client';

import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useUserProvider } from '@wt/game/providers/user/UserProvider';
import { BackButton } from '@wt/shared/components/BackButton';
import { useCopyToClipboard } from '@wt/shared/hooks/useCopyClipboard';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { browserName, isDesktop, osName } from 'react-device-detect';
import { toast } from 'react-toastify';
import useWebShare from 'react-use-web-share';

import { getAverage, getStreak } from '../utils';
import {
  getSiteMode,
  getSiteName,
  getSiteURL,
  hasHints,
} from '@wt/utilities/siteMode';
import EnhancedDualToggleHintChart from './EnhancedDualToggleHintChart';
import { useParams } from 'next/navigation';

export const StatsContent = () => {
  const params = useParams();
  const { user, loggedIn } = useUserProvider();
  const [, copyShareText] = useCopyToClipboard();
  const { isSupported, share } = useWebShare();
  const [isLegacy, setLegacy] = useState(false);

  useEffect(() => {
    if (window.location.hash == '#legacy') {
      setLegacy(true);
    } else if (isLegacy) {
      setLegacy(false);
    }
  }, [params]);

  const streak = useMemo(() => {
    return getStreak({
      currentStreak: user.currentStreak,
      lastGamePlayed: user.lastGamePlayed,
    });
  }, [user.currentStreak, user.lastGamePlayed]);

  const totalAverage = useMemo(() => {
    return getAverage({
      totalScore: user.totalLocationScore + user.totalYearScore,
      gamesPlayed: user.gamesPlayed,
    });
  }, [user.totalLocationScore, user.totalYearScore, user.gamesPlayed]);

  const yearAverage = useMemo(() => {
    return getAverage({
      totalScore: user.totalYearScore,
      gamesPlayed: user.gamesPlayed,
    });
  }, [user.totalYearScore, user.gamesPlayed]);

  const locationAverage = useMemo(() => {
    return getAverage({
      totalScore: user.totalLocationScore,
      gamesPlayed: user.gamesPlayed,
    });
  }, [user.totalLocationScore, user.gamesPlayed]);

  const shareText = useMemo(() => {
    const statsStrings =
      getSiteMode() == 'whentaken-movies'
        ? [
            'Check out my stats on movies.whentaken.com',
            `🔥 ${streak} | 🎲 ${user.gamesPlayed}`,
            `⚡️ Total:  🌐 ${user.highestTotalScore} 🌀 ${totalAverage}`,
            `🗓️ Year:  🌐 ${user.highestYearScore} 🌀 ${yearAverage}`,
            `📍 Location:  🌐 ${user.highestLocationScore} 🌀 ${locationAverage}`,
          ]
        : getSiteMode() == 'wheretaken'
          ? [
              'Check out my stats on whentaken.com',
              `🔥 ${streak} | 🎲 ${user.gamesPlayed}`,
              `⚡️ Total:  🌐 ${user.highestTotalScore} 🌀 ${totalAverage}`,
              `🗓️ Year:  🌐 ${user.highestYearScore} 🌀 ${yearAverage}`,
              `📍 Location:  🌐 ${user.highestLocationScore} 🌀 ${locationAverage}`,
            ]
          : [
              'Check out my stats on whentaken.com',
              `🔥 ${streak} | 🎲 ${user.gamesPlayed}`,
              `⚡️ Total:  🌐 ${user.highestTotalScore} 🌀 ${totalAverage}`,
              `🗓️ Year:  🌐 ${user.highestYearScore} 🌀 ${yearAverage}`,
              `📍 Location:  🌐 ${user.highestLocationScore} 🌀 ${locationAverage}`,
            ];

    return statsStrings.join('\n');
  }, [user]);

  const shareStats = () => {
    // Firefox Android does not support navigator.share with text
    const isFirefoxAndroid = browserName === 'Firefox' && osName === 'Android';

    if (isSupported && !isFirefoxAndroid && !isDesktop) {
      share({
        title: `${getSiteName()} Stats`,
        text: shareText,
        url: getSiteURL(),
      });
    } else {
      copyShareText(shareText).then((c) => {
        toast(c ? 'Copied to clipboard' : 'Failed to copy to clipboard', {
          type: c ? 'success' : 'error',
        });
      });
    }
    return;
  };

  return (
    <div className="mb-5 mt-3 flex w-full flex-col items-center justify-center px-4 md:my-5">
      <div className="flex w-full flex-col items-center gap-3 max-w-[364px] wheretaken:md:max-w-[364px] md:max-w-[728px]">
        <div className="relative mb-2 flex w-full items-center justify-center">
          <BackButton />
          <div className="w-full text-center text-xl md:text-2xl">My stats</div>
        </div>
        {!loggedIn && <WarningContainer />}
        <div className="flex w-full flex-row gap-4">
          <SmallStatsContainer emoji={'🔥'} stat={streak} label={'Streak'} />
          <SmallStatsContainer
            emoji={'🎲'}
            stat={user.gamesPlayed}
            label={'Games played'}
          />
        </div>
        {hasHints() && getSiteMode() != 'wheretaken' && !isLegacy ? (
          <>
            <EnhancedDualToggleHintChart />
          </>
        ) : (
          <>
            <TopAndAverageScoreStatsContainer
              label={getSiteMode() != 'wheretaken' ? '⚡️ Total' : '⚡️ Score'}
              topScore={user.highestTotalScore}
              averageScore={totalAverage}
            />
            {getSiteMode() != 'wheretaken' && (
              <>
                <TopAndAverageScoreStatsContainer
                  label={'🗓️ Year'}
                  topScore={user.highestYearScore}
                  averageScore={yearAverage}
                />
                <TopAndAverageScoreStatsContainer
                  label={'📍 Location'}
                  topScore={user.highestLocationScore}
                  averageScore={locationAverage}
                />
              </>
            )}
            {hasHints() && getSiteMode() != 'wheretaken' && isLegacy && (
              <p>
                <Link
                  href={``}
                  className="hover:underline hover:decoration-dotted text-sm text-gray-300 text-center whentaken:text-highlight"
                >
                  Switch to new stats view
                </Link>
              </p>
            )}
            <ShareStatsButton shareStats={shareStats} />
          </>
        )}
      </div>
    </div>
  );
};

const SmallStatsContainer = ({
  emoji,
  stat,
  label,
}: {
  emoji: string;
  stat: number;
  label: string;
}) => {
  return (
    <div className="flex w-full flex-col items-center justify-center gap-2 rounded-2xl bg-base-200 p-4 text-center">
      <span className="text-3xl font-bold">{`${emoji} ${stat}`}</span>
      <span className="text-sm md:text-base">{label}</span>
    </div>
  );
};

const TopAndAverageScoreStatsContainer = ({
  label,
  topScore,
  averageScore,
}: {
  label: string;
  topScore: number;
  averageScore: number;
}) => {
  return (
    <div className="flex w-full flex-col items-center justify-center gap-2 rounded-2xl bg-base-200 p-4 text-center">
      <span className="text-sm font-semibold md:text-lg">{label}</span>
      <div className="flex w-full flex-row justify-center gap-4">
        <div className="flex w-1/3 flex-col gap-2 md:w-[28%]">
          <span className="text-3xl font-bold">{topScore}</span>
          <span className="text-sm md:text-base">{'🌐 Top'}</span>
        </div>
        <div className="flex w-1/3 flex-col gap-2 md:w-[28%]">
          <span className="text-3xl font-bold">{averageScore}</span>
          <span className="text-sm md:text-base">{'🌀 Average'}</span>
        </div>
      </div>
    </div>
  );
};

const ShareStatsButton = ({ shareStats }: { shareStats: () => void }) => {
  return (
    <button
      onClick={shareStats}
      className="btn btn-primary mt-2 h-[40px] min-h-[40px] w-fit shrink whitespace-nowrap rounded-lg text-base font-medium md:text-xl"
    >
      <span className="pointer-events-none whitespace-nowrap">
        <ShareOutlinedIcon className="pr-1" /> Share stats
      </span>
    </button>
  );
};

const WarningContainer = () => {
  return (
    <div className="mb-3 flex w-full flex-col items-center justify-center overflow-hidden rounded-2xl bg-[#FFEECC] text-base">
      <div className="flex w-full flex-row items-center justify-center bg-[#FFCC6A] py-1 font-semibold text-[#322E38]">
        <WarningAmberRoundedIcon className="pr-1" /> Warning
      </div>
      <div className="flex flex-col items-center gap-2 px-3 py-2 text-center text-sm text-[#4B4554] md:text-base">
        <span>
          You are not logged in, so your stats will not be synced throughout the
          different devices and browsers.
        </span>
        <span>
          Log in or sign up to start saving your gaming stats and playing across
          multiple devices.
        </span>
        <LoginButton />
      </div>
    </div>
  );
};

const LoginButton = () => {
  return (
    <Link
      onClick={() => {
        localStorage.setItem('post-login-location', window.location.pathname);
      }}
      href={'/login'}
      className="btn h-[40px] min-h-[40px] w-fit shrink rounded-xl border border-gray-400 bg-base-300 text-sm font-normal text-base-100 hover:bg-base-300/80 md:text-base"
    >
      <span className="pointer-events-none whitespace-nowrap">
        <LoginRoundedIcon className="pr-1" /> Log in or sign up
      </span>
    </Link>
  );
};
